import { Decimal } from './decimal';
import { formaterPourcentage } from './formatage';
import type { CategorieOpcvm, Poche } from './referentiel';

/**
 * Règles de cohérence d'une allocation cible (doc 01, § 5.2).
 *
 * Fonction pure : elle valide côté serveur et alimente l'écran d'édition
 * en direct, sans qu'une divergence puisse s'installer entre les deux.
 * Le moteur d'allocation (lot 4) consommera les mêmes structures.
 */

export const STATUTS_ALLOCATION = [
  'BROUILLON',
  'A_VALIDER',
  'VALIDEE',
  'REMPLACEE',
  'REJETEE',
] as const;
export type StatutAllocation = (typeof STATUTS_ALLOCATION)[number];

export interface LigneAllocation {
  poche: Poche;
  /** Poche ACTIONS : instrument précis. */
  instrumentId?: number | null;
  /** Poche TAUX : tranche de maturité, jamais une ligne BDT. */
  trancheMaturiteId?: number | null;
  /** Poche OPCVM : catégorie AMMC. */
  categorieOpcvm?: CategorieOpcvm | null;
  pourcentage: string | Decimal;
  /** Libellé d'affichage, pour des messages compréhensibles. */
  libelle?: string;
}

export interface ContexteAllocation {
  profilCharia: boolean;
  /** Instruments référencés en poche actions, indexés par identifiant. */
  actions: Map<number, { libelle: string; statut: string; chariaCompliant: boolean }>;
  /**
   * Catégories OPCVM pour lesquelles il existe au moins un fonds conforme
   * à la charia. Sans cela, une allocation charia pourrait désigner une
   * catégorie qu'aucun fonds éligible ne peut servir.
   */
  categoriesAvecFondsCharia?: Set<CategorieOpcvm>;
  /** Contrôles supplémentaires appliqués au moment de valider. */
  pourValidation: boolean;
}

export type CodeAnomalieAllocation =
  | 'SOMME_INVALIDE'
  | 'DOUBLON'
  | 'POURCENTAGE_INVALIDE'
  | 'REFERENCE_MANQUANTE'
  | 'INSTRUMENT_INDISPONIBLE'
  | 'CHARIA_NON_CONFORME'
  | 'ALLOCATION_VIDE';

export interface AnomalieAllocation {
  code: CodeAnomalieAllocation;
  message: string;
}

/** Tolérance d'arrondi sur la somme : un centième de point. */
const TOLERANCE = new Decimal('0.01');

function cleDe(ligne: LigneAllocation): string {
  if (ligne.poche === 'ACTIONS') return `ACTIONS:${ligne.instrumentId}`;
  if (ligne.poche === 'TAUX') return `TAUX:${ligne.trancheMaturiteId}`;
  return `OPCVM:${ligne.categorieOpcvm}`;
}

function designation(ligne: LigneAllocation): string {
  return ligne.libelle ?? cleDe(ligne);
}

export function validerAllocation(
  lignes: readonly LigneAllocation[],
  contexte: ContexteAllocation,
): AnomalieAllocation[] {
  const anomalies: AnomalieAllocation[] = [];

  if (lignes.length === 0) {
    return [
      { code: 'ALLOCATION_VIDE', message: 'Une allocation doit comporter au moins une ligne.' },
    ];
  }

  const vues = new Set<string>();

  for (const ligne of lignes) {
    // Règle 2 — la référence doit correspondre à la poche.
    const referenceValide =
      (ligne.poche === 'ACTIONS' &&
        ligne.instrumentId != null &&
        ligne.trancheMaturiteId == null &&
        ligne.categorieOpcvm == null) ||
      (ligne.poche === 'TAUX' &&
        ligne.trancheMaturiteId != null &&
        ligne.instrumentId == null &&
        ligne.categorieOpcvm == null) ||
      (ligne.poche === 'OPCVM' &&
        ligne.categorieOpcvm != null &&
        ligne.instrumentId == null &&
        ligne.trancheMaturiteId == null);

    if (!referenceValide) {
      anomalies.push({
        code: 'REFERENCE_MANQUANTE',
        message: `Ligne ${ligne.poche} : la référence ne correspond pas à la poche.`,
      });
      continue;
    }

    const cle = cleDe(ligne);
    if (vues.has(cle)) {
      anomalies.push({
        code: 'DOUBLON',
        message: `${designation(ligne)} figure deux fois dans la poche ${ligne.poche}.`,
      });
    }
    vues.add(cle);

    // Règle 3 — pourcentage strictement positif et au plus 100.
    const pourcentage = new Decimal(ligne.pourcentage.toString());
    if (
      !pourcentage.isFinite() ||
      pourcentage.lessThanOrEqualTo(0) ||
      pourcentage.greaterThan(100)
    ) {
      anomalies.push({
        code: 'POURCENTAGE_INVALIDE',
        message: `${designation(ligne)} : le pourcentage doit être strictement supérieur à 0 et au plus égal à 100.`,
      });
    }

    if (ligne.poche === 'ACTIONS' && ligne.instrumentId != null) {
      const action = contexte.actions.get(ligne.instrumentId);
      if (!action) {
        anomalies.push({
          code: 'REFERENCE_MANQUANTE',
          message: `${designation(ligne)} : instrument inconnu.`,
        });
      } else {
        // Règle 5 — un instrument indisponible ne peut pas être alloué.
        if (contexte.pourValidation && action.statut !== 'ACTIF') {
          anomalies.push({
            code: 'INSTRUMENT_INDISPONIBLE',
            message: `${action.libelle} est ${action.statut.toLowerCase()} et ne peut pas figurer dans une allocation validée.`,
          });
        }
        // Règle 4 — conformité charia.
        if (contexte.profilCharia && !action.chariaCompliant) {
          anomalies.push({
            code: 'CHARIA_NON_CONFORME',
            message: `${action.libelle} n'est pas conforme à la charia.`,
          });
        }
      }
    }

    if (
      ligne.poche === 'OPCVM' &&
      ligne.categorieOpcvm != null &&
      contexte.profilCharia &&
      contexte.categoriesAvecFondsCharia &&
      !contexte.categoriesAvecFondsCharia.has(ligne.categorieOpcvm)
    ) {
      anomalies.push({
        code: 'CHARIA_NON_CONFORME',
        message: `Aucun OPCVM conforme à la charia n'existe dans la catégorie ${ligne.categorieOpcvm} : cette ligne ne pourrait jamais être exécutée.`,
      });
    }
  }

  // Règle 1 — la somme vaut 100 %.
  const total = lignes.reduce(
    (acc, l) => acc.plus(new Decimal(l.pourcentage.toString() || '0')),
    new Decimal(0),
  );
  if (total.minus(100).abs().greaterThan(TOLERANCE)) {
    anomalies.push({
      code: 'SOMME_INVALIDE',
      message: `La somme des lignes vaut ${formaterPourcentage(total)}, elle doit valoir 100 %.`,
    });
  }

  return anomalies;
}

/** Poids total d'une poche, utile à l'affichage comme au moteur. */
export function poidsParPoche(lignes: readonly LigneAllocation[]): Record<Poche, Decimal> {
  const totaux: Record<Poche, Decimal> = {
    ACTIONS: new Decimal(0),
    TAUX: new Decimal(0),
    OPCVM: new Decimal(0),
  };
  for (const ligne of lignes) {
    totaux[ligne.poche] = totaux[ligne.poche].plus(
      new Decimal(ligne.pourcentage.toString() || '0'),
    );
  }
  return totaux;
}

export interface EcartVersions {
  cle: string;
  libelle: string;
  poche: Poche;
  avant: Decimal | null;
  apres: Decimal | null;
  variation: Decimal;
}

/**
 * Compare deux versions d'allocation. Sert à montrer au gérant ce que sa
 * nouvelle version change avant qu'il la valide — et donc l'ampleur de la
 * campagne de réajustement qu'elle déclenchera sur tous les mandats.
 */
export function comparerAllocations(
  avant: readonly LigneAllocation[],
  apres: readonly LigneAllocation[],
): EcartVersions[] {
  const index = new Map<string, EcartVersions>();

  const enregistrer = (ligne: LigneAllocation, cote: 'avant' | 'apres') => {
    const cle = cleDe(ligne);
    const existant = index.get(cle) ?? {
      cle,
      libelle: designation(ligne),
      poche: ligne.poche,
      avant: null,
      apres: null,
      variation: new Decimal(0),
    };
    existant[cote] = new Decimal(ligne.pourcentage.toString());
    if (ligne.libelle) existant.libelle = ligne.libelle;
    index.set(cle, existant);
  };

  avant.forEach((l) => enregistrer(l, 'avant'));
  apres.forEach((l) => enregistrer(l, 'apres'));

  return (
    [...index.values()]
      .map((e) => ({
        ...e,
        variation: (e.apres ?? new Decimal(0)).minus(e.avant ?? new Decimal(0)),
      }))
      .filter((e) => !e.variation.isZero())
      // Ordre entièrement déterministe : ampleur décroissante, puis les
      // hausses avant les baisses, puis l'alphabet. À égalité d'ampleur, un
      // ordre arbitraire ferait bouger le tableau d'un affichage à l'autre.
      .sort(
        (a, b) =>
          b.variation.abs().comparedTo(a.variation.abs()) ||
          b.variation.comparedTo(a.variation) ||
          a.libelle.localeCompare(b.libelle, 'fr'),
      )
  );
}
